import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumericFormat } from 'react-number-format';
import cx from 'classnames';
import { Plan } from '@/api';
import { PlanPeriods } from '@/config';
import { INDIVIDUAL_PRO_PLUS_PLAN_VALUE } from '../../Tariffs.constants';

import styles from './OrderSummary.module.sass';

export type OrderSummaryPrices = {
  price: number;
  pricePerCheck: number;
  oldPrice: number;
  discount: number;
};

interface OrderSummaryProps {
  tariff: Plan;
  prices: OrderSummaryPrices;
  className?: string;
}

const PERIOD_KEYS: Record<PlanPeriods, string> = {
  [PlanPeriods.YEAR]: 'translation.order_summary_period_year',
  [PlanPeriods.MONTH]: 'translation.order_summary_period_month',
  [PlanPeriods.UNLIMITED]: 'translation.order_summary_period_unlimited',
};

export const OrderSummary = ({
  tariff,
  prices,
  className,
}: OrderSummaryProps) => {
  const { t } = useTranslation();

  const numberFormat = useNumericFormat({
    thousandSeparator: ',',
    decimalScale: 2,
  });

  const money = (value: number) =>
    `$${numberFormat.format?.(value.toString())}`;

  const creditsCount = useMemo(() => {
    if (tariff.value === INDIVIDUAL_PRO_PLUS_PLAN_VALUE) return tariff.value;
    return numberFormat.format?.((Number(tariff.value) || 0).toString());
  }, [tariff.value]);

  const creditsLabel =
    Number(tariff.value) === 1
      ? t('translation.checks_without_count_singular')
      : t('translation.checks_without_count_plural');

  const hasDiscount = prices.discount > 0 && prices.oldPrice > prices.price;
  const discountAmount =
    Math.round((prices.oldPrice - prices.price) * 100) / 100;

  const rows = [
    {
      label: t('translation.order_summary_price_per_credit'),
      value: money(prices.pricePerCheck),
    },
    {
      label: t('translation.order_summary_list_price'),
      value: money(prices.oldPrice),
      className: hasDiscount ? styles.strike : undefined,
    },
    ...(hasDiscount
      ? [
          {
            label: t('translation.order_summary_discount'),
            value: `-${money(discountAmount)} (${numberFormat.format?.(
              prices.discount.toString(),
            )}%)`,
            className: styles.discount,
          },
        ]
      : []),
    {
      label: t('translation.order_summary_valid_for'),
      value: t(
        PERIOD_KEYS[tariff.periodicity] ?? PERIOD_KEYS[PlanPeriods.YEAR],
      ),
    },
  ];

  return (
    <div className={cx(styles.receipt, className)}>
      <div className={styles.frame}>
        <div className={styles.paper}>
          <div className={styles.head}>
            <h3 className={styles.title}>
              {t('translation.order_summary_title')}
            </h3>
            <div className={styles.credits}>
              <span className={styles.creditsCount}>{creditsCount}</span>
              <span className={styles.creditsLabel}>{creditsLabel}</span>
            </div>
          </div>

          <dl className={styles.rows}>
            {rows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt className={styles.rowLabel}>{row.label}</dt>
                <span className={styles.leader} aria-hidden="true" />
                <dd className={cx(styles.rowValue, row.className)}>
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <div className={styles.total}>
            <span className={styles.totalLabel}>
              {t('translation.order_summary_total')}
            </span>
            <span className={styles.totalValue}>{money(prices.price)}</span>
          </div>

          <p className={styles.note}>{t('translation.order_summary_note')}</p>
        </div>
      </div>
    </div>
  );
};
